/**
 * Formulation 流式会话逻辑
 */
import { nextTick, type Ref } from 'vue';
import type { PsHttpSSE } from '@patsnap-utils/sse';
import type {
  AgentMessageResponse,
  AiRefineRequest,
  FormulationRequest,
  ResumeFormulationRequest,
} from '@/types/formulation/formulation';
import { TASK_STATUS } from '../types';
import { hasExistingUserInput } from '../message-utils';
import { getStreamPayload } from './stream-payload';
import { createTaskPoller, type TaskPoller } from './task-poller';
import { createMessageUpdater } from './message-updater';
import { createHandlerManager } from './handlers';
import { createListAgentMessagePoller } from './list-message-poller';
import type { StreamConversationDeps, StreamContext, HandlerContext } from './types';

export type { StreamConversationDeps } from './types';

interface ActiveStreamRun {
  sessionIdValue: string;
  stream: PsHttpSSE<string>;
  poller: TaskPoller | null;
  listMessagePollerStop: (() => void) | null;
  resolve: (() => void) | null;
  settled: boolean;
}

/**
 * 创建 streamConversation 函数
 */
export function createStreamConversation(deps: StreamConversationDeps) {
  const {
    sessionId,
    conversationData,
    currentIteration,
    selectedToolKey,
    pipelineRunning,
    taskStatus,
    shouldScrollToBottom,
    materialFormulationApiService,
    setSelectedTool,
    tryAutoOpenLastToolResult,
    clearPendingUserMessage,
    onSseConnectionError,
  } = deps;

  // 创建处理器管理器
  const handlerManager = createHandlerManager();
  let activeRun: ActiveStreamRun | null = null;

  const isActiveRun = (run: ActiveStreamRun) =>
    activeRun === run && !run.settled && sessionId.value === run.sessionIdValue;

  const settleRun = (run: ActiveStreamRun, options: { closeStream: boolean }) => {
    if (run.settled) return;

    // Revoke ownership before closing so late callbacks are ignored.
    run.settled = true;
    if (activeRun === run) {
      activeRun = null;
    }
    run.listMessagePollerStop?.();
    run.listMessagePollerStop = null;
    run.poller?.stop();
    run.poller = null;

    if (sessionId.value === run.sessionIdValue) {
      pipelineRunning.value = false;
    }

    if (options.closeStream) {
      run.stream.close();
    }

    run.resolve?.();
    run.resolve = null;
  };

  /** Close the active browser stream without stopping the backend task. */
  const closeActiveStream = () => {
    const run = activeRun;
    if (!run) return;

    settleRun(run, { closeStream: true });
  };

  /**
   * 流式会话处理
   */
  const streamConversation = async (
    sessionIdValue: string,
    contextIdValue: Ref<number> | undefined,
    content: string,
    options?: {
      skipAddUserMessage?: boolean;
      aiRefine?: AiRefineRequest;
      /** Answer a pending_user_input question via /resume instead of starting a new turn. */
      resume?: ResumeFormulationRequest;
    },
  ) => {
    if (sessionId.value !== sessionIdValue) return;

    closeActiveStream();

    let stream;
    if (options?.resume) {
      stream = materialFormulationApiService.resumeSession(sessionIdValue, options.resume);
    } else {
      const request: FormulationRequest = { user_input: content };
      const effectiveContextId = contextIdValue?.value;
      if (effectiveContextId) {
        (request as FormulationRequest & { context_id?: number }).context_id = effectiveContextId;
      }
      if (options?.aiRefine) {
        request.ai_refine = options.aiRefine;
      }
      stream = materialFormulationApiService.formulate(sessionIdValue, request);
    }

    const run: ActiveStreamRun = {
      sessionIdValue,
      stream,
      poller: null,
      listMessagePollerStop: null,
      resolve: null,
      settled: false,
    };
    activeRun = run;
    pipelineRunning.value = true;

    const existing = conversationData.value ?? [];
    const shouldAddUserMessage =
      !options?.skipAddUserMessage && !hasExistingUserInput(existing, content);
    const roundStartIso = new Date().toISOString();
    const streamIteration = Math.max(1, (currentIteration.value || 0) + 1);

    // 添加用户消息
    if (shouldAddUserMessage) {
      const userMessage: AgentMessageResponse = {
        id: Date.now(),
        clientKey: String(Date.now()),
        business_type: 'user_input',
        session_id: sessionIdValue,
        iteration: streamIteration,
        tool_key: 'user_input',
        content: { raw: content },
        tool_state: TASK_STATUS.COMPLETED,
        gmt_created: roundStartIso,
      };
      conversationData.value = [...existing, userMessage];
      clearPendingUserMessage();
      await nextTick();
      if (!isActiveRun(run)) return;
      shouldScrollToBottom.value = true;
    }

    if (!isActiveRun(run)) return;

    taskStatus.value = TASK_STATUS.RUNNING;

    // 创建轮询器
    const poller = createTaskPoller({
      sessionId,
      taskStatus,
      pipelineRunning,
      api: materialFormulationApiService,
    });
    run.poller = poller;
    poller.start(sessionIdValue);

    // 创建流上下文
    const streamContext: StreamContext = {
      sessionIdValue,
      streamIteration,
      roundStartIso,
      deps,
    };

    // 创建消息更新器
    const messageUpdater = createMessageUpdater(
      { conversationData, selectedToolKey, setSelectedTool },
      streamContext,
    );

    return new Promise<void>((resolve) => {
      run.resolve = resolve;

      /** 结束流式处理 */
      const finishStreamAndFetchFinal = () => {
        if (!isActiveRun(run)) return;

        // 使用流式过程中已积累的 conversationData 打开最后一个工具结果
        tryAutoOpenLastToolResult(conversationData.value);
        settleRun(run, { closeStream: true });
      };

      // 创建处理器上下文
      const handlerContext: HandlerContext = {
        messageUpdater,
        streamContext,
        finishStream: finishStreamAndFetchFinal,
      };

      stream
        .onMessage((m) => {
          if (!isActiveRun(run)) return;

          try {
            const data = getStreamPayload(m);
            if (!data) return;

            handlerManager.handleMessage(data, handlerContext);
          } catch (error) {
            console.error('[Formulation] Failed to parse stream message:', error, m);
          }
        })
        .onFinish(() => {
          if (!isActiveRun(run)) return;

          // SSE 连接关闭时结束流
          console.debug('[Formulation] SSE stream finished');
          finishStreamAndFetchFinal();
        })
        .onError((error) => {
          if (!isActiveRun(run)) return;

          console.error('[Formulation] SSE stream error:', error);
          poller.stop();
          pipelineRunning.value = false;
          if (onSseConnectionError?.(error)) {
            finishStreamAndFetchFinal();
            return;
          }
          // SSE 异常时改为轮询 listAgentMessages 同步消息，直到任务结束再 finish
          const listMessagePoller = createListAgentMessagePoller(
            sessionIdValue,
            {
              sessionId,
              taskStatus,
              conversationData,
              materialFormulationApiService,
              tryAutoOpenLastToolResult,
            },
            finishStreamAndFetchFinal,
          );
          run.listMessagePollerStop = listMessagePoller.stop;
        });

      // The SDK reports failures through onError and also rejects start(); consume that rejection
      // so intentional close() calls do not surface as unhandled promise rejections.
      Promise.resolve(stream.start()).catch(() => undefined);
    });
  };

  return {
    streamConversation,
    closeActiveStream,
  };
}
